import { render } from 'preact'
import { installAudio } from './audio'
import { initAuth, localGuestName } from './auth/supabase'
import type { AuthSession } from './auth/supabase'
import { cosmetics } from './cosmetics'
import { Store } from './net/store'
import { startErrorReporting } from './observability/errors'
import type { ErrorContext } from './observability/errors'
import { shareLinkCode } from './party/shareLink'
import { WebAdapter } from './platform/WebAdapter'
import { App } from './ui/App'
import { isRoomless } from './ui/flow'
import type { PracticeResult } from './ui/podium/podiumData'
import { chunkFailed, installChunkReload, pageReload } from './ui/reload/pageReload'
import { ReloadButton } from './ui/reload/ReloadButton'
import { Session } from './ui/session'
import { clearTryOnForRaces } from './ui/shop/tryOn'

function mustGet(id: string): HTMLElement {
  const el = document.getElementById(id)
  if (!el) throw new Error(`#${id} missing from index.html`)
  return el
}

const params = new URLSearchParams(window.location.search)
// A stale deploy's missing chunk reloads the page once (D-066).
installChunkReload()
// Sound unlocks on the first gesture; M mutes (PHASE3 T3.6).
installAudio(params.has('debug'))

function reportErrors(context: () => ErrorContext): void {
  startErrorReporting({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: import.meta.env.VITE_RELEASE,
    environment: import.meta.env.MODE,
    context,
  })
}

if (params.has('practice')) {
  // Local practice race (D-023): no server, no sign-in. Loaded on demand so the sim and the
  // track data stay out of the menu bundle. Its podium's panel is Preact, like online (T3.5), and
  // so is the race's Leave bar (Escape asks first, D-066).
  const result = new Store<PracticeResult | null>(null)
  let racing = false
  // A chunk failing mid-race waits for the podium before the page reloads (D-066).
  pageReload.setBusy(() => racing && result.get() === null)
  result.subscribe((r) => {
    if (r) pageReload.idle()
  })
  void Promise.all([import('./game/practice'), import('./ui/PracticeScreen')]).then(
    async ([{ startPractice }, { PracticeScreen }]) => {
      const practice = await startPractice(mustGet('game'), {
        debug: params.has('debug'),
        autoplay: params.has('autoplay'),
        onResult: (r) => result.set(r),
      })
      racing = true
      render(
        <PracticeScreen
          result={result}
          onRaceAgain={() => practice.restart()}
          onLeave={() => window.location.assign(import.meta.env.BASE_URL)}
        />,
        mustGet('ui'),
      )
    },
    (err: unknown) => {
      // Nothing else is on this page: offer Reload when the page will not reload by itself.
      render(<ReloadButton fill />, mustGet('ui'))
      chunkFailed(err)
    },
  )
  reportErrors(() => ({ screen: 'race', mode: 'practice', userId: null }))
} else {
  // Menu → lobby → race → results (T1.8). Sign in early so the first join has a token.
  const platform = new WebAdapter()
  const auth = initAuth({
    url: import.meta.env.VITE_SUPABASE_URL,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  })
  // Read at each report: sign-out changes the user without a reload (T2.6).
  let signedIn: AuthSession | null = null
  void auth.then((a) => (signedIn = a))
  const session = new Session({
    serverUrl: import.meta.env.VITE_SERVER_URL || 'ws://localhost:2567',
    name: localGuestName(),
    auth,
    platform,
    gameHost: mustGet('game'),
    race: { debug: params.has('debug'), autoplay: params.has('autoplay') },
    // Dev aid only: extra round trip to test prediction under latency.
    lagMs: import.meta.env.DEV ? Number(params.get('lag') ?? 0) || 0 : 0,
    // The runner picked in the hub goes out with every join (PHASE3 T3.1).
    charId: () => cosmetics.charId.get(),
    // So does the ride equipped in the Rides tab (T3.11).
    ride: () => cosmetics.equippedRide.get(),
  })
  // A party reserves our race seats with our join options: tell it when the runner or ride changes
  // (T2.7, T3.11).
  cosmetics.charId.subscribe(() => session.party.refreshOptions())
  cosmetics.equippedRide.subscribe(() => session.party.refreshOptions())
  // A ride tried on in the shop shows on the hub only, until a join leaves it (T3.9a).
  clearTryOnForRaces(session.flow, cosmetics.tryOnRide)
  // Never reload mid-race (or in a lobby or the results): wait for the menu (D-066).
  pageReload.setBusy(() => !isRoomless(session.flow.get()))
  session.flow.subscribe((f) => {
    if (isRoomless(f)) pageReload.idle()
  })
  void platform.init()
  render(<App session={session} />, mustGet('ui'))
  // A share link `/r/<CODE>` (T2.7): open that party or private lobby, and show `/` from now on.
  const linkCode = shareLinkCode(window.location.pathname)
  if (linkCode !== undefined) {
    const { search, hash } = window.location
    window.history.replaceState(window.history.state, '', `/${search}${hash}`)
    session.openShareLink(linkCode)
  }
  reportErrors(() => ({
    screen: session.flow.get().screen,
    mode: session.auth.get() === 'offline' ? 'offline' : 'online',
    userId: signedIn?.userId ?? null,
  }))
}
